import type { AppliedFrame, AppliedRow } from './applied-frame';
import { encounterCreatureField } from '../../shared/array-types';
import { readInventoryField, readInventoryRows } from '../compile/inventory-raw-readers';
import { readItemField } from '../compile/item-field-reader';
import { statusRows } from '../statuses/expiry';

const upper = (value: unknown): string => String(value ?? '').toUpperCase();

/** A birth/departure frame still carries the exact linked status/inventory writes.
 * Surviving members outside that footprint keep their entire held render tree. */
export function deletionMemberUnchanged(frame: AppliedFrame): ((member: AppliedRow) => boolean) | undefined {
    if (!frame.new_entities.length && !frame.new_inventory?.length && !frame.deletes.some(row => row.table === 'encounter_creatures')) return;
    const touched = new Set([
        ...Object.keys(frame.member_revs).map(upper), ...Object.keys(frame.computed).map(upper), ...Object.keys(frame.computed_delta ?? {}).map(upper),
        ...Object.keys(frame.inputs?.member_catalogs ?? {}).map(upper),
        ...frame.deletes.map(row => upper(row.row_id)), ...frame.new_entities.flatMap(row => [upper(row.entity_id),
            upper(encounterCreatureField(row.member.encounter_creatures, 'controller_encounter_creature_id'))]),
        ...(frame.new_inventory ?? []).map(row => upper(row.possess_id)),
    ]);
    for (const change of frame.rows) {
        touched.add(upper(change.row_id));
        for (const key of ['possess_id', 'status_target_id', 'status_caster_id', 'charge_holder', 'caster_encounter_creature_id',
            'controller_encounter_creature_id']) touched.add(upper(readItemField(change.row, key)));
    }
    touched.delete('');
    return member => {
        const pending = [member], seen = new Set<AppliedRow>();
        while (pending.length) {
            const row = pending.pop()!;
            if (seen.has(row)) continue;
            seen.add(row);
            const summons = row.caster_summons ?? (row.encounter_creatures as AppliedRow | undefined)?.caster_summons;
            if (Array.isArray(summons) && summons.some(summon => summon && typeof summon === 'object'
                && touched.has(upper(readItemField(summon as AppliedRow, 'encounter_creature_id'))))) return false;
            if (touched.has(upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id')))
                || touched.has(upper(readInventoryField(row, 'inventory_id')))
                || touched.has(upper(readItemField((row.player_characters as AppliedRow | undefined) ?? null, 'item_id')))
                || ['target', 'caster'].some(side => statusRows(row, side as 'target' | 'caster')
                    .some(status => touched.has(upper(readItemField(status, 'status_id')))))
                || Array.isArray(row.counterspell_watches) && row.counterspell_watches.some(watch => watch && typeof watch === 'object'
                    && touched.has(upper(readItemField(watch as AppliedRow, 'trigger_id'))))) return false;
            for (const key of ['inventory', 'inventory_in_container', 'encounter_inventories_arr'] as const)
                pending.push(...readInventoryRows(row, key));
        }
        return true;
    };
}
